import type * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type ButtonProps = React.ComponentProps<typeof Button>;

/** Any icon component, not just Lucide's — some icons are composed locally
 *  (the health meter, for instance) but behave the same here. */
type IconComponent = React.ComponentType<{
  className?: string;
  "aria-hidden"?: React.AriaAttributes["aria-hidden"];
}>;

export interface IconActionProps
  extends Omit<ButtonProps, "children" | "aria-label" | "size" | "asChild"> {
  /** The accessible name, and the tooltip text unless `tooltip` overrides it. */
  label: string;
  icon: IconComponent;
  /** Visible tooltip, for the rare case it should read differently to the name. */
  tooltip?: React.ReactNode;
  size?: "icon" | "icon-xs" | "icon-sm" | "icon-lg";
  /** Renders the control as an external link instead of a button. */
  href?: string;
  /** A Button-compatible component to render instead, e.g. ConfirmationButton. */
  as?: React.ComponentType<ButtonProps>;
}

/**
 * An icon-only control whose tooltip text is also its accessible name.
 *
 * A Radix tooltip only contributes `aria-describedby`, which screen readers
 * read *after* the name — so an icon button with a tooltip and no label still
 * announces as just "button". Routing both through one prop is what keeps them
 * from drifting apart.
 */
export function IconAction({
  label,
  icon: Icon,
  tooltip,
  size = "icon",
  href,
  as: Component = Button,
  ...props
}: IconActionProps) {
  // Icon size is left to the button variant, which scales it per size
  // (size-4 normally, size-3 for icon-xs).
  const control = href ? (
    <Button size={size} aria-label={label} asChild {...props}>
      <a href={href} target="_blank" rel="noopener">
        <Icon aria-hidden="true" />
      </a>
    </Button>
  ) : (
    <Component size={size} aria-label={label} {...props}>
      <Icon aria-hidden="true" />
    </Component>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{control}</TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}
